import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronRight,
  RotateCw,
  Search,
  AlertCircle,
  Info,
} from 'lucide-react';
import { api } from '@/api/client';
import type { QuotaMatchesResp } from '@/api/types';
import { cn } from '@/lib/utils';
import { extractApiMessage } from '@/components/resources/form/errors';

export default function QuotaMatchesPage() {
  const { name: nameParam = '' } = useParams<{ name: string }>();
  const [search, setSearch] = useState('');

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['quota-matches', nameParam],
    queryFn: () =>
      api<QuotaMatchesResp>(
        `/v1/quotas/${encodeURIComponent(nameParam)}/matches`,
      ),
    enabled: nameParam !== '',
    refetchOnWindowFocus: true,
  });

  const matches = data?.matches;
  const rows = matches?.matches ?? [];

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return rows;
    return rows.filter(
      (m) =>
        m.cluster_id.toLowerCase().includes(needle) ||
        m.user.toLowerCase().includes(needle) ||
        m.project.toLowerCase().includes(needle) ||
        m.department.toLowerCase().includes(needle) ||
        m.allocated_resources.toLowerCase().includes(needle),
    );
  }, [rows, search]);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0"
          >
            <Link
              to="/quotas"
              className="hover:text-foreground transition-colors shrink-0 inline-flex items-center gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Quotas
            </Link>
            <ChevronRight
              className="h-3.5 w-3.5 shrink-0 opacity-60"
              aria-hidden
            />
            <span className="text-foreground truncate">{nameParam}</span>
            <ChevronRight
              className="h-3.5 w-3.5 shrink-0 opacity-60"
              aria-hidden
            />
            <span className="text-foreground">Matches</span>
          </nav>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 transition-colors shrink-0"
          >
            <RotateCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
        </div>
        <h1
          className="text-2xl font-semibold tracking-tight text-primary font-mono truncate"
          title={nameParam}
        >
          {nameParam}
        </h1>
        {matches?.description && (
          <p className="text-sm text-muted-foreground">
            {matches.description}
          </p>
        )}
      </div>

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading matches…
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Failed to load matches</div>
            <div className="text-xs mt-0.5 opacity-90">
              {extractApiMessage(error)}
            </div>
          </div>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {rows.length > 0 && (
            <div className="relative max-w-md">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by cluster, user, project, or department"
                className="w-full pl-9 pr-3 py-2 rounded-md border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-colors"
              />
            </div>
          )}

          <div className="border rounded-lg overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2.5 font-medium">Cluster</th>
                  <th className="px-3 py-2.5 font-medium">User</th>
                  <th className="px-3 py-2.5 font-medium">Project</th>
                  <th className="px-3 py-2.5 font-medium">Department</th>
                  <th className="px-3 py-2.5 font-medium">Allocated resources</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-16 text-center text-muted-foreground text-sm"
                    >
                      <div className="inline-flex items-center gap-2">
                        <Info className="h-4 w-4 opacity-70" aria-hidden />
                        No allocations currently match this quota.
                      </div>
                    </td>
                  </tr>
                )}
                {rows.length > 0 && filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-12 text-center text-muted-foreground text-sm"
                    >
                      No matches for the current filter.
                    </td>
                  </tr>
                )}
                {filtered.map((m, i) => (
                  <tr
                    key={`${m.cluster_id}-${i}`}
                    className="border-t hover:bg-muted/10 transition-colors"
                  >
                    <td className="px-3 py-1.5 font-mono text-xs break-all">
                      {m.cluster_id || '—'}
                    </td>
                    <td className="px-3 py-1.5 font-medium">
                      {m.user || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">
                      {m.project || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">
                      {m.department || '—'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs">
                      {m.allocated_resources || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
